import { useEffect, useMemo, useState } from 'react';
import type { EChartsType } from 'echarts/core';
import debounce from 'lodash/debounce';

import type { TimeSeriesChartProps } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';

import { interactionConfig } from '../config/interaction';
import type { TimeDomain } from '../types/TimeDomain.types';

type UseVisibleRangeParams = Pick<TimeSeriesChartProps, 'timeDomain'> & {
  chart: EChartsType | undefined;
};

/** Viewport as percentages of the full domain, the way ECharts reports data zoom changes. */
type ViewportPercent = {
  start: number;
  end: number;
};

type DataZoomEvent = Partial<ViewportPercent> & {
  batch?: Array<Partial<ViewportPercent>>;
};

const FULL_VIEWPORT: ViewportPercent = { start: 0, end: 100 };

type ViewportState = ViewportPercent & {
  domainStart: number;
  domainEnd: number;
};

function isDataZoomEvent(event: unknown): event is DataZoomEvent {
  return typeof event === 'object' && event !== null;
}

/** ECharts reports the inside zoom through `batch` and the slider directly on the event. */
function readViewport(event: unknown, previous: ViewportPercent): ViewportPercent {
  if (!isDataZoomEvent(event)) {
    return previous;
  }

  const change = event.batch?.[0] ?? event;

  return {
    start: typeof change.start === 'number' ? change.start : previous.start,
    end: typeof change.end === 'number' ? change.end : previous.end,
  };
}

/** The stored viewport while it belongs to the current domain; a new domain starts fully zoomed out. */
function getViewportForDomain(state: ViewportState, domainStart: number, domainEnd: number): ViewportPercent {
  return state.domainStart === domainStart && state.domainEnd === domainEnd ? state : FULL_VIEWPORT;
}

function toTimeRange(domainStart: number, domainEnd: number, { start, end }: ViewportPercent): TimeDomain {
  const span = domainEnd - domainStart;

  return [domainStart + (span * start) / 100, domainStart + (span * end) / 100];
}

/**
 * The chart's current time window, derived from the full domain and the settled zoom percentage.
 * A single `datazoom` subscription backs every viewport-dependent decision (symbol visibility,
 * the detailed part of each line), so the chart is rebuilt once per gesture, not once per consumer.
 *
 * The window updates only after the gesture has settled (see interactionConfig.viewportSettleMs).
 * Every event of a burst is still applied in order, so a partial event (only `start`, say) lands
 * on top of the ones before it exactly as if each had been applied on its own.
 */
export function useVisibleRange({ chart, timeDomain }: UseVisibleRangeParams): TimeDomain {
  const { startTimestamp: domainStart, endTimestamp: domainEnd } = timeDomain;

  const [viewportState, setViewportState] = useState<ViewportState>(() => ({
    ...FULL_VIEWPORT,
    domainStart,
    domainEnd,
  }));
  const { start, end } = getViewportForDomain(viewportState, domainStart, domainEnd);

  useEffect(() => {
    if (!chart || chart.isDisposed()) {
      return;
    }

    let pendingEvents: unknown[] = [];

    const applyPendingEvents = debounce(() => {
      const events = pendingEvents;

      pendingEvents = [];

      setViewportState((previousState) => ({
        ...events.reduce<ViewportPercent>(
          (viewport, event) => readViewport(event, viewport),
          getViewportForDomain(previousState, domainStart, domainEnd)
        ),
        domainStart,
        domainEnd,
      }));
    }, interactionConfig.viewportSettleMs);

    const handleDataZoom = (event: unknown) => {
      pendingEvents.push(event);
      applyPendingEvents();
    };

    chart.on('datazoom', handleDataZoom);

    return () => {
      chart.off('datazoom', handleDataZoom);
      applyPendingEvents.cancel();
    };
  }, [chart, domainStart, domainEnd]);

  // Keyed on the primitive bounds: the returned tuple feeds the option memo, so a fresh array per
  // render would rebuild the whole option and call setOption on every parent re-render.
  return useMemo(() => toTimeRange(domainStart, domainEnd, { start, end }), [domainStart, domainEnd, start, end]);
}
